import './polyfills';

import React from 'react';
import ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import Spinner from '@atlaskit/spinner';

import ErrorBoundary from './components/ErrorBoundary';
import ServerErrorInterceptor from './containers/ServerErrorInterceptor';
import UnSupportedMobileBrowserInterceptor from './containers/UnSupportedMobileBrowserInterceptor';
import { initClient as initErrorReportingClient } from './utilities/analytics/error-reporting';
import LocaleSwitcher from './components/LocaleSwitcher';
import {
  buildInitialState,
  createAnalyticsClient,
  startupAnalyticEvent,
  pushGlobals,
  initCastle,
} from './utilities/init-app-state';
import App from './containers/App';
import { startBrowserMetrics, BrowserMetricsBoundary } from './browser-metrics';
import AnalyticsClientProvider from './containers/AnalyticsClientProvider';
import { createAppStore } from './stores/redux.store';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

// Render a spinner to remove the "failed to load scripts" error while loading the app
const container = document.getElementById('root');

ReactDOM.render(
  <div style={{ display: 'flex', height: '100%', justifyContent: 'center', alignItems: 'center' }}>
    <Spinner size="large" />
  </div>,
  container
);

buildInitialState().then(initialState => {
  const { initialStoreState, featureFlags, cspNonce } = initialState;

  initErrorReportingClient(initialStoreState.microbranding, featureFlags || {});

  const analyticsClient = createAnalyticsClient(initialState);

  startupAnalyticEvent(analyticsClient);

  startBrowserMetrics(analyticsClient);

  pushGlobals(initialStoreState.appConfig, featureFlags, cspNonce);

  const store = createAppStore(initialStoreState);

  if (initialStoreState.appConfig) {
    initCastle(initialStoreState.appConfig);
  }

  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        retry: false,
      },
    },
  });

  ReactDOM.render(
    <Provider store={store}>
      <LocaleSwitcher>
        <ErrorBoundary analyticsClient={analyticsClient}>
          <QueryClientProvider client={queryClient}>
            <BrowserRouter>
              <AnalyticsClientProvider>
                <ServerErrorInterceptor>
                  <UnSupportedMobileBrowserInterceptor>
                    <BrowserMetricsBoundary>
                      <App />
                    </BrowserMetricsBoundary>
                  </UnSupportedMobileBrowserInterceptor>
                </ServerErrorInterceptor>
              </AnalyticsClientProvider>
            </BrowserRouter>
          </QueryClientProvider>
        </ErrorBoundary>
      </LocaleSwitcher>
    </Provider>,
    container
  );
});
